import { memo, type PropsWithChildren } from 'react';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks';
import { ExclamationmarkSmallCircleColorIcon, RefreshIcon } from '@/icons';
import { Button } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { Bubble } from './Bubble';
import { splitContentLines } from './splitContentLines';
import { type ChatMessageType } from './types';
type Props = Pick<ChatMessageType, 'role' | 'name' | 'avatarSrc'>;
const ChatMessage = ({
  role,
  name,
  avatarSrc,
  children
}: PropsWithChildren<Props>) => {
  return <div className={cn('flex gap-sm pt-2xs pb-xs', role === 'AI' ? 'pl-md pr-2xl' : 'flex-row-reverse pl-4xl pr-md')} data-sentry-component="ChatMessage" data-sentry-source-file="ChatMessage.tsx">
      {avatarSrc && <img className="size-[26px]" src={avatarSrc} alt={name ?? 'AI'} width={26} height={26} />}
      <div className={cn('flex flex-col gap-3xs', {
      'items-end': role === 'USER'
    })}>
        {name && <span className="text-body-sm-medium text-alternative mobile-lg:text-caption-lg-medium">
            {name}
          </span>}
        {children}
      </div>
    </div>;
};
type AiChatMessageProps = Pick<ChatMessageType, 'name' | 'avatarSrc' | 'content' | 'isLoading' | 'isError'> & {
  loadingText?: string;
  withRetryButton?: boolean;
  onRetry?: VoidFunction;
  className?: string;
};
export const AiChatMessage = memo(({
  name,
  avatarSrc,
  content,
  isLoading,
  loadingText,
  isError,
  withRetryButton,
  onRetry,
  className
}: AiChatMessageProps) => {
  const {
    t
  } = useTranslation(['play']);
  return <ChatMessage role="AI" name={name} avatarSrc={avatarSrc}>
        {(() => {
      if (isLoading) {
        return <p className="text-body-lg-medium gradient-text-shine mobile-lg:text-body-md-medium">
                {loadingText}
              </p>;
      }
      if (isError) {
        return <div className="flex items-center gap-3xs">
                <ExclamationmarkSmallCircleColorIcon className="[&>path:first-child]:fill-assistive [&>path:last-child]:fill-white" size="xs" />
                <p className="text-body-lg-medium text-neutral mobile-lg:text-body-md-medium">
                  {t('play:aiTutor.error')}
                </p>
              </div>;
      }
      return <ul className="flex flex-col items-start gap-2xs">
              {splitContentLines(content).map((line, i) => <Bubble key={i} className={className} role="AI" content={line} />)}
              {withRetryButton && <RetryButton onClick={onRetry} />}
            </ul>;
    })()}
      </ChatMessage>;
});
AiChatMessage.displayName = 'AiChatMessage';
const RetryButton = ({
  onClick
}: {
  onClick?: VoidFunction;
}) => {
  const {
    t
  } = useTranslation(['play']);
  const {
    isMobile
  } = useViewport();
  return <Button variant="transparent" intent="neutral" size={isMobile ? '2xs' : '3xs'} leftSlot={<RefreshIcon size={isMobile ? 'xs' : '2xs'} />} onClick={onClick} data-sentry-element="Button" data-sentry-component="RetryButton" data-sentry-source-file="ChatMessage.tsx">
      {t('play:aiTutor.retry')}
    </Button>;
};
type UserChatMessageProps = Pick<ChatMessageType, 'content'> & {
  className?: string;
};
export const UserChatMessage = memo(({
  content,
  className
}: UserChatMessageProps) => <ChatMessage role="USER">
      <Bubble className={className} role="USER" content={content} />
    </ChatMessage>);
UserChatMessage.displayName = 'UserChatMessage';